/**
 * SwapTab - Reusable token swap component
 *
 * Features:
 * - SOL/USDC swapping via Jupiter Aggregator
 * - Real-time quote fetching with debounce
 * - Gas reserve protection for SOL swaps
 * - Password-protected swap execution
 * - Configurable slippage tolerance
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiService, { SwapBalances, SwapRequest, SwapQuote } from '../../services/api';

interface SwapTabProps {
  onError: (error: string | null) => void;
  onSuccess: (message: string | null) => void;
  onBalanceChange?: () => void;
  isDesktop?: boolean;
}

const SwapTab: React.FC<SwapTabProps> = ({
  onError,
  onSuccess,
  onBalanceChange,
  isDesktop = false,
}) => {
  // Swap state
  const [swapBalances, setSwapBalances] = useState<SwapBalances | null>(null);
  const [swapHistory, setSwapHistory] = useState<SwapRequest[]>([]);
  const [swapInputToken, setSwapInputToken] = useState<'SOL' | 'USDC'>('SOL');
  const [swapAmount, setSwapAmount] = useState('');
  const [swapQuote, setSwapQuote] = useState<SwapQuote | null>(null);
  const [swapPassword, setSwapPassword] = useState('');
  const [swapSlippage, setSwapSlippage] = useState(50); // 0.5%
  const [fetchingQuote, setFetchingQuote] = useState(false);
  const [swapSubmitting, setSwapSubmitting] = useState(false);
  const [pendingSwapId, setPendingSwapId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSwapData();
  }, []);

  const fetchSwapData = async () => {
    setLoading(true);
    try {
      const [balancesData, historyData] = await Promise.all([
        apiService.swap.getBalances(),
        apiService.swap.getList(),
      ]);
      setSwapBalances(balancesData);
      setSwapHistory(historyData);
    } catch (err) {
      console.error('Failed to fetch swap data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch swap quote when amount changes
  useEffect(() => {
    const fetchQuote = async () => {
      if (!swapAmount || parseFloat(swapAmount) <= 0) {
        setSwapQuote(null);
        return;
      }

      const amountNum = parseFloat(swapAmount);
      const outputToken = swapInputToken === 'SOL' ? 'USDC' : 'SOL';

      try {
        setFetchingQuote(true);
        const quote = await apiService.swap.getQuote({
          input_token: swapInputToken,
          output_token: outputToken,
          input_amount: amountNum,
          slippage_bps: swapSlippage,
        });
        setSwapQuote(quote);
      } catch (err) {
        console.error('Failed to fetch quote:', err);
        setSwapQuote(null);
      } finally {
        setFetchingQuote(false);
      }
    };

    const timer = setTimeout(fetchQuote, 500);
    return () => clearTimeout(timer);
  }, [swapAmount, swapInputToken, swapSlippage]);

  // Poll for pending swap status
  useEffect(() => {
    if (!pendingSwapId) return;

    const pollSwap = async () => {
      try {
        const swap = await apiService.swap.getDetail(pendingSwapId);
        if (swap.status === 'completed') {
          onSuccess(`Swap completed! Received ${swap.actual_output_amount?.toFixed(swap.output_token === 'SOL' ? 4 : 2)} ${swap.output_token}`);
          setPendingSwapId(null);
          await fetchSwapData();
          onBalanceChange?.();
        } else if (swap.status === 'failed') {
          onError(`Swap failed: ${swap.error_message || 'Unknown error'}`);
          setPendingSwapId(null);
        }
      } catch (err) {
        console.error('Failed to poll swap status:', err);
      }
    };

    const interval = setInterval(pollSwap, 2000);
    return () => clearInterval(interval);
  }, [pendingSwapId, onError, onSuccess, onBalanceChange]);

  const handleSwapSetMax = () => {
    if (swapBalances) {
      const maxAmount = swapInputToken === 'SOL' ? swapBalances.max_swap_sol : swapBalances.max_swap_usdc;
      setSwapAmount(maxAmount.toFixed(swapInputToken === 'SOL' ? 4 : 2));
    }
  };

  const handleSwapToggle = () => {
    setSwapInputToken(swapInputToken === 'SOL' ? 'USDC' : 'SOL');
    setSwapAmount('');
    setSwapQuote(null);
  };

  const handleSwapSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onError(null);
    onSuccess(null);

    const amountNum = parseFloat(swapAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    if (!swapPassword) {
      onError('Please enter your password to authorize the swap');
      return;
    }

    if (!swapQuote) {
      onError('Please wait for the quote to load');
      return;
    }

    const outputToken = swapInputToken === 'SOL' ? 'USDC' : 'SOL';

    try {
      setSwapSubmitting(true);
      const swap = await apiService.swap.create({
        input_token: swapInputToken,
        output_token: outputToken,
        input_amount: amountNum,
        slippage_bps: swapSlippage,
        password: swapPassword,
      });

      onSuccess('Swap submitted! Processing transaction...');
      setPendingSwapId(swap.id);
      setSwapAmount('');
      setSwapPassword('');
      setSwapQuote(null);
      await fetchSwapData();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      onError(axiosError.response?.data?.error || 'Failed to submit swap');
    } finally {
      setSwapSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'pending':
      case 'processing': return 'text-yellow-400';
      case 'failed':
      case 'cancelled': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <motion.div
          className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  // Desktop layout - two column grid
  if (isDesktop) {
    return (
      <div className="grid grid-cols-1 desktop-lg:grid-cols-2 gap-8">
        {/* Swap Form */}
        <div className="bg-gray-900 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Swap Tokens</h3>

          <form onSubmit={handleSwapSubmit} className="space-y-5">
            {/* From Token */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                From
              </label>
              <div className="flex gap-3">
                <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl">
                  <span className="font-semibold text-white min-w-[50px]">{swapInputToken}</span>
                  <input
                    type="number"
                    step={swapInputToken === 'SOL' ? '0.0001' : '0.01'}
                    value={swapAmount}
                    onChange={(e) => setSwapAmount(e.target.value)}
                    placeholder="0.00"
                    className="flex-1 bg-transparent text-white text-right focus:outline-none"
                  />
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleSwapSetMax}
                  className="px-6 py-3 bg-gray-800 hover:bg-gray-700
                           border border-gray-700 text-white font-semibold rounded-xl"
                >
                  MAX
                </motion.button>
              </div>
            </div>

            {/* Swap Toggle */}
            <div className="flex justify-center">
              <motion.button
                whileTap={{ scale: 0.9 }}
                type="button"
                onClick={handleSwapToggle}
                className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </motion.button>
            </div>

            {/* To Token */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                To (Estimated)
              </label>
              <div className="flex items-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl">
                <span className="font-semibold text-white min-w-[50px]">{swapInputToken === 'SOL' ? 'USDC' : 'SOL'}</span>
                <span className="flex-1 text-right text-white">
                  {fetchingQuote ? (
                    <span className="text-gray-500">Loading...</span>
                  ) : swapQuote ? (
                    swapQuote.output_amount.toFixed(swapInputToken === 'SOL' ? 2 : 4)
                  ) : (
                    <span className="text-gray-500">0.00</span>
                  )}
                </span>
              </div>
            </div>

            {/* Quote Details */}
            {swapQuote && (
              <div className="bg-black/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Exchange Rate</span>
                  <span className="text-white">1 {swapInputToken} = {swapQuote.exchange_rate.toFixed(swapInputToken === 'SOL' ? 2 : 4)} {swapInputToken === 'SOL' ? 'USDC' : 'SOL'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Price Impact</span>
                  <span className={swapQuote.price_impact_percent > 1 ? 'text-yellow-400' : 'text-white'}>
                    {swapQuote.price_impact_percent.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Slippage Tolerance</span>
                  <span className="text-white">{(swapSlippage / 100).toFixed(1)}%</span>
                </div>
              </div>
            )}

            {/* Slippage Setting */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Slippage Tolerance
              </label>
              <div className="flex gap-2">
                {[50, 100, 200].map((bps) => (
                  <motion.button
                    key={bps}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setSwapSlippage(bps)}
                    className={`flex-1 py-2 rounded-xl font-semibold transition-colors ${
                      swapSlippage === bps
                        ? 'bg-white text-black'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {(bps / 100).toFixed(1)}%
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Password
              </label>
              <input
                type="password"
                value={swapPassword}
                onChange={(e) => setSwapPassword(e.target.value)}
                placeholder="Enter your account password"
                className="w-full px-4 py-3 bg-black border border-gray-700
                         text-white rounded-xl focus:outline-none focus:border-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Required to authorize the swap
              </p>
            </div>

            {/* Submit */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={swapSubmitting || !swapAmount || !swapPassword || !swapQuote || pendingSwapId !== null}
              className="w-full py-4 bg-white hover:bg-gray-100
                       text-black font-bold rounded-xl transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {swapSubmitting || pendingSwapId ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : `Swap ${swapInputToken} → ${swapInputToken === 'SOL' ? 'USDC' : 'SOL'}`}
            </motion.button>
          </form>
        </div>

        {/* Available Balance & History */}
        <div className="space-y-6">
          {swapBalances && (
            <div className="bg-gray-900 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Available Balance</h3>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-black/50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">SOL</p>
                  <p className="text-3xl font-bold text-white">{swapBalances.available_sol.toFixed(4)}</p>
                  <p className="text-xs text-gray-500 mt-1">Max: {swapBalances.max_swap_sol.toFixed(4)}</p>
                </div>
                <div className="bg-black/50 rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">USDC</p>
                  <p className="text-3xl font-bold text-white">${swapBalances.available_usdc.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">Max: ${swapBalances.max_swap_usdc.toFixed(2)}</p>
                </div>
              </div>
              {swapInputToken === 'SOL' && (
                <div className="pt-4 border-t border-gray-800 text-sm">
                  <p className="text-gray-500">
                    Note: {swapBalances.gas_reserve_sol} SOL reserved for gas fees
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recent Swaps Table */}
          {swapHistory.length > 0 && (
            <div className="bg-gray-900 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Swaps</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-800">
                      <th className="pb-3">Swap</th>
                      <th className="pb-3">Date</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {swapHistory.slice(0, 5).map((s) => (
                      <tr key={s.id} className="border-b border-gray-800/50 last:border-0">
                        <td className="py-3 text-white font-mono text-sm">
                          {Number(s.input_amount).toFixed(s.input_token === 'SOL' ? 4 : 2)} {s.input_token} → {s.actual_output_amount ? Number(s.actual_output_amount).toFixed(s.output_token === 'SOL' ? 4 : 2) : s.quoted_output_amount?.toFixed(s.output_token === 'SOL' ? 4 : 2) || '?'} {s.output_token}
                        </td>
                        <td className="py-3 text-gray-400 text-sm">
                          {new Date(s.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          <span className={`text-sm font-semibold ${getStatusColor(s.status)}`}>
                            {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mobile layout - single column
  return (
    <>
      {/* Available Balance */}
      {swapBalances && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-900 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-sm font-medium text-gray-500 mb-4">Available Balance</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">SOL</p>
              <p className="text-2xl font-bold text-white">{swapBalances.available_sol.toFixed(4)}</p>
              <p className="text-xs text-gray-500">Max swap: {swapBalances.max_swap_sol.toFixed(4)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">USDC</p>
              <p className="text-2xl font-bold text-white">${swapBalances.available_usdc.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Max swap: ${swapBalances.max_swap_usdc.toFixed(2)}</p>
            </div>
          </div>
          {swapInputToken === 'SOL' && (
            <div className="pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500">
                Note: {swapBalances.gas_reserve_sol} SOL reserved for gas fees
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Swap Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-900 rounded-2xl p-6 mb-6"
      >
        <form onSubmit={handleSwapSubmit} className="space-y-5">
          {/* From Token */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              From
            </label>
            <div className="flex gap-3">
              <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl">
                <span className="font-semibold text-white">{swapInputToken}</span>
                <input
                  type="number"
                  step={swapInputToken === 'SOL' ? '0.0001' : '0.01'}
                  value={swapAmount}
                  onChange={(e) => setSwapAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 bg-transparent text-white text-right focus:outline-none"
                />
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={handleSwapSetMax}
                className="px-4 py-3 bg-gray-800 hover:bg-gray-700
                         border border-gray-700 text-white font-semibold rounded-xl"
              >
                MAX
              </motion.button>
            </div>
          </div>

          {/* Swap Toggle */}
          <div className="flex justify-center">
            <motion.button
              whileTap={{ scale: 0.9 }}
              type="button"
              onClick={handleSwapToggle}
              className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </motion.button>
          </div>

          {/* To Token */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              To (Estimated)
            </label>
            <div className="flex items-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl">
              <span className="font-semibold text-white">{swapInputToken === 'SOL' ? 'USDC' : 'SOL'}</span>
              <span className="flex-1 text-right text-white">
                {fetchingQuote ? (
                  <span className="text-gray-500">Loading...</span>
                ) : swapQuote ? (
                  swapQuote.output_amount.toFixed(swapInputToken === 'SOL' ? 2 : 4)
                ) : (
                  <span className="text-gray-500">0.00</span>
                )}
              </span>
            </div>
          </div>

          {/* Quote Details */}
          {swapQuote && (
            <div className="bg-black/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Exchange Rate</span>
                <span className="text-white">1 {swapInputToken} = {swapQuote.exchange_rate.toFixed(swapInputToken === 'SOL' ? 2 : 4)} {swapInputToken === 'SOL' ? 'USDC' : 'SOL'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Price Impact</span>
                <span className={swapQuote.price_impact_percent > 1 ? 'text-yellow-400' : 'text-white'}>
                  {swapQuote.price_impact_percent.toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Slippage Tolerance</span>
                <span className="text-white">{(swapSlippage / 100).toFixed(1)}%</span>
              </div>
            </div>
          )}

          {/* Slippage Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Slippage Tolerance
            </label>
            <div className="flex gap-2">
              {[50, 100, 200].map((bps) => (
                <motion.button
                  key={bps}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => setSwapSlippage(bps)}
                  className={`flex-1 py-2 rounded-xl font-semibold transition-colors ${
                    swapSlippage === bps
                      ? 'bg-white text-black'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {(bps / 100).toFixed(1)}%
                </motion.button>
              ))}
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Password
            </label>
            <input
              type="password"
              value={swapPassword}
              onChange={(e) => setSwapPassword(e.target.value)}
              placeholder="Enter your account password"
              className="w-full px-4 py-3 bg-black border border-gray-700
                       text-white rounded-xl focus:outline-none focus:border-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Required to authorize the swap
            </p>
          </div>

          {/* Submit */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={swapSubmitting || !swapAmount || !swapPassword || !swapQuote || pendingSwapId !== null}
            className="w-full py-4 bg-white hover:bg-gray-100
                     text-black font-bold rounded-xl transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {swapSubmitting || pendingSwapId ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : `Swap ${swapInputToken} → ${swapInputToken === 'SOL' ? 'USDC' : 'SOL'}`}
          </motion.button>
        </form>
      </motion.div>

      {/* Recent Swaps */}
      {swapHistory.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-900 rounded-2xl p-6"
        >
          <h3 className="text-sm font-medium text-gray-500 mb-4">Recent Swaps</h3>
          <div className="space-y-4">
            {swapHistory.slice(0, 5).map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
              >
                <div>
                  <p className="text-white font-mono">
                    {Number(s.input_amount).toFixed(s.input_token === 'SOL' ? 4 : 2)} {s.input_token} → {s.actual_output_amount ? Number(s.actual_output_amount).toFixed(s.output_token === 'SOL' ? 4 : 2) : s.quoted_output_amount?.toFixed(s.output_token === 'SOL' ? 4 : 2) || '?'} {s.output_token}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(s.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${getStatusColor(s.status)}`}>
                    {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </>
  );
};

export default SwapTab;
